import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
  Tooltip,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { getTargetLanguageFromPair } from '../utils/languagePairs';
import dayjs, { Dayjs } from 'dayjs';
import type { ChapterWordCounts, Chapter } from '../types';
import type { AskAIInvocationPayload } from '../AskAIModal';
import './AdminDashboardSurfaces.css';

const { Option } = Select;
const { Text } = Typography;

interface TranslationCostStageEntry {
  stage: string;
  costUsd?: number;
  promptTokens?: number;
  completionTokens?: number;
  modelIds?: string[];
  subStages?: Array<{
    label: string;
    costUsd?: number;
    promptTokens?: number;
    completionTokens?: number;
    durationSeconds?: number;
    firstTimestamp?: string;
    lastTimestamp?: string;
  }>;
  durationSeconds?: number;
  firstTimestamp?: string;
  lastTimestamp?: string;
}

interface TranslationCostRunEntry {
  id?: string;
  runId?: string;
  chapterGuid: string;
  targetLang: string;
  totalCostUsd: number;
  translationCostUsd?: number;
  validationCostUsd?: number;
  proofreaderCostUsd?: number;
  alignmentCostUsd?: number;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  stageBreakdown?: TranslationCostStageEntry[];
  processingSeconds?: number;
  alignmentFinalRan?: boolean;
}

interface TranslationCostSummaryResponse {
  bookId: string;
  targetLang?: string;
  totalCostUsd: number;
  entries: TranslationCostRunEntry[];
}

interface TranslationIntegrityScanProgress {
  totalChapters: number;
  completedChapters: number;
  failedChapters: number;
}

interface TranslationIntegrityScanSummary {
  highRiskCount: number;
  mediumRiskCount: number;
  lowRiskCount: number;
  cleanCount: number;
}

interface TranslationIntegrityScanRecord {
  scanId: string;
  bookId: string;
  sourceLang: string;
  targetLang: string;
  mode: string;
  status: string;
  requestedBy?: string;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  updatedAt?: string;
  progress: TranslationIntegrityScanProgress;
  summary: TranslationIntegrityScanSummary;
  error?: string | null;
  version?: string | null;
}

interface TranslationIntegrityScanListResponse {
  items: TranslationIntegrityScanRecord[];
  count: number;
}

interface TranslationIntegrityLlmAdjudication {
  status?: string;
  modelId?: string;
  missingContentLikely?: boolean;
  confidence?: number;
  reason?: string;
  coverageSummary?: string;
  missingEvidence?: string[];
  error?: string;
}

interface TranslationIntegrityFinding {
  chapterGuid: string;
  chapterName?: string;
  riskLevel: 'high' | 'medium' | 'low' | 'clean' | string;
  riskScore: number;
  signals?: string[];
  metrics?: {
    sourceParagraphCount?: number;
    targetParagraphCount?: number;
    paragraphDeltaPct?: number;
    sourceCharCount?: number;
    targetCharCount?: number;
    charDeltaPct?: number;
    sourceSentenceCount?: number;
    targetSentenceCount?: number;
    sentenceCoverageEstimate?: number;
  };
  evidence?: {
    missingLikeSpans?: string[];
    validationReasons?: string[];
    latestOutcomeSignals?: string[];
    llmAdjudication?: TranslationIntegrityLlmAdjudication;
  };
  error?: string | null;
}

interface TranslationIntegrityScanFindingsResponse {
  items: TranslationIntegrityFinding[];
  summary: TranslationIntegrityScanSummary;
  total: number;
  offset: number;
  limit: number;
  nextOffset?: number | null;
}

interface ChapterSuggestionOption {
  value: string;
  label: string;
  guid: string;
}

interface AdminTranslationInsightsProps {
  book: any;
  bookId: string;
  apiBase: string;
  auth: any;
  chapterWordCounts: ChapterWordCounts;
  isActive: boolean;
  isAdmin: boolean;
  onAskAI?: (payload: AskAIInvocationPayload) => void;
}

interface AdminLogReplacement {
  from?: string;
  to?: string;
  count?: number;
  reason?: string;
  applied?: boolean;
  details?: string;
  method?: string;
  status?: string;
}

interface AdminLogCostInfo {
  inputCostPerMTokens?: number;
  outputCostPerMTokens?: number;
  currency?: string;
  actualCostUsd?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  reasoningTokens?: number;
  cachedTokens?: number;
}

interface AlignmentDetails {
  reason?: string;
  source_excerpt?: string;
  target_excerpt?: string;
  [key: string]: any;
}

interface AdminLogEntry {
  timestamp?: string;
  stage: string;
  stageCategory?: string;
  runId?: string;
  provider?: string;
  model?: string;
  configuredModelId?: string;
  modelDisplayName?: string;
  apiModel?: string;
  modelRole?: string;
  status?: string;
  snippet?: string;
  reply?: string;
  replacements?: AdminLogReplacement[];
  sourceLanguage?: string;
  targetLanguage?: string;
  bookId?: string;
  chapterGuid?: string;
  chunkId?: number;
  alignmentDetails?: AlignmentDetails;
  alignmentCheckId?: string;
  fullText?: string;
  task?: string;
  statusSummary?: string;
  cost?: AdminLogCostInfo;
  stageLabel?: string;
  system_prompt_preview?: string;
  user_prompt_preview?: string;
}

type AdminLogStageCategory =
  | 'translation'
  | 'validator'
  | 'proofreader_formality'
  | 'proofreader_general'
  | 'proofreader_alignment'
  | 'other'
  | 'combined';

type PipelineStepKey =
  | 'translation'
  | 'alignment_post_translation'
  | 'validation'
  | 'proofreader_formality'
  | 'proofreader_general'
  | 'alignment_final';

interface ChapterPipelineStep {
  key: PipelineStepKey;
  label: string;
  entries: AdminLogEntry[];
  status?: string;
  startTimestamp?: string;
  endTimestamp?: string;
  durationSeconds?: number;
}

interface ChapterPipelineRun {
  id: string;
  runId?: string;
  chapterGuid?: string;
  chapterName?: string;
  targetLanguage?: string;
  sourceLanguage?: string;
  wordCount?: number;
  startTimestamp?: string;
  endTimestamp?: string;
  status?: string;
  chunkCount: number;
  totalEntries: number;
  pipelineSteps: ChapterPipelineStep[];
  entries: AdminLogEntry[];
}

const PIPELINE_STEP_DEFINITIONS: Array<{ key: PipelineStepKey; label: string }> = [
  { key: 'translation', label: 'Translation' },
  { key: 'alignment_post_translation', label: 'Alignment (Post-Translation)' },
  { key: 'validation', label: 'Validation' },
  { key: 'proofreader_formality', label: 'Formality Proofread' },
  { key: 'proofreader_general', label: 'General Proofread' },
  { key: 'alignment_final', label: 'Final Alignment' },
];

const PIPELINE_STEP_LABELS = PIPELINE_STEP_DEFINITIONS.reduce<
  Record<PipelineStepKey, string>
>((acc, { key, label }) => {
  acc[key] = label;
  return acc;
}, {} as Record<PipelineStepKey, string>);

const ALIGNMENT_SUB_STAGE_LABELS: Record<string, string> = {
  posttranslate: 'Alignment Check (post-translation)',
  final: 'Alignment Check (post-proofread)',
  error: 'Alignment Check (error recovery)',
  nonsuccess: 'Alignment Check (non-success)',
  noissues: 'Alignment Check (no replacements)',
};

const normalizeStageKey = (value?: string) =>
  (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const formatStageHeading = (value?: string) => {
  const key = normalizeStageKey(value);
  if (key === 'alignment') {
    return 'Alignment Checks';
  }
  return (value || 'stage').replace(/_/g, ' ');
};

const formatSubStageLabel = (stageName?: string, label?: string) => {
  if (!label) {
    return 'step';
  }
  if (normalizeStageKey(stageName) === 'alignment') {
    const key = normalizeStageKey(label);
    return (
      ALIGNMENT_SUB_STAGE_LABELS[key] ||
      `Alignment (${label.replace(/_/g, ' ')})`
    );
  }
  return label.replace(/_/g, ' ');
};

const formatAdminLogTimestamp = (value?: string) =>
  value ? dayjs(value).format('MMM D, YYYY h:mm:ss A') : '—';

const formatUsd = (value?: number) =>
  typeof value === 'number' ? `$${value.toFixed(4)}` : '$0.0000';

const formatDuration = (seconds?: number) => {
  if (!seconds || Number.isNaN(seconds)) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = seconds / 60;
  if (mins < 60) {
    return `${mins.toFixed(1)}m`;
  }
  return `${(mins / 60).toFixed(1)}h`;
};

const getIntegrityRiskTagColor = (riskLevel?: string) => {
  const normalized = (riskLevel || '').trim().toLowerCase();
  if (normalized === 'high') {
    return 'red';
  }
  if (normalized === 'medium') {
    return 'orange';
  }
  if (normalized === 'low') {
    return 'gold';
  }
  return 'green';
};

const categorizeLogEntry = (entry: AdminLogEntry): AdminLogStageCategory => {
  const normalizedStageCategory = (entry.stageCategory || '').trim().toLowerCase();
  const stage = (entry.stage || '').trim().toLowerCase();
  const role = (entry.modelRole || '').trim().toLowerCase();
  const task = (entry.task || '').trim().toLowerCase();
  const combined = `${normalizedStageCategory} ${stage} ${role} ${task}`;

  if (combined.includes('validator')) {
    return 'validator';
  }
  if (combined.includes('alignment')) {
    return 'proofreader_alignment';
  }
  if (combined.includes('general')) {
    return 'proofreader_general';
  }
  if (combined.includes('formality') || combined.includes('proofreader')) {
    return 'proofreader_formality';
  }
  if (
    combined.includes('translation') ||
    combined.includes('translate') ||
    combined.includes('chunk') ||
    combined.includes('assemble') ||
    combined.includes('persist') ||
    combined.includes('proofread_before')
  ) {
    return 'translation';
  }
  return 'other';
};

const determineAlignmentPipelineStepKey = (entry: AdminLogEntry): PipelineStepKey => {
  const stageKey = normalizeStageKey(entry.stage);
  const taskKey = normalizeStageKey(entry.task);
  const labelKey = normalizeStageKey(entry.stageLabel);
  const combined = `${stageKey} ${taskKey} ${labelKey}`.trim();
  if (
    combined.includes('posttranslate') ||
    combined.includes('posttranslation') ||
    combined.includes('post_translation') ||
    combined.includes('postproofreadbefore')
  ) {
    return 'alignment_post_translation';
  }
  if (combined.includes('final') || combined.includes('postproofread') || combined.includes('finalalignment')) {
    return 'alignment_final';
  }
  if (
    combined.includes('error') ||
    combined.includes('nonsuccess') ||
    combined.includes('noissues') ||
    combined.includes('redo')
  ) {
    return 'alignment_final';
  }
  return 'alignment_final';
};

const determinePipelineStepKey = (entry: AdminLogEntry): PipelineStepKey | null => {
  const category = categorizeLogEntry(entry);
  if (category === 'translation') {
    return 'translation';
  }
  if (category === 'validator') {
    return 'validation';
  }
  if (category === 'proofreader_formality') {
    return 'proofreader_formality';
  }
  if (category === 'proofreader_general') {
    return 'proofreader_general';
  }
  if (category === 'proofreader_alignment') {
    return determineAlignmentPipelineStepKey(entry);
  }
  return null;
};

const normalizeStatusText = (value?: string) => (value || '').trim().toLowerCase();

const getStatusTagColor = (status?: string, hasEntries?: boolean) => {
  if (!status) {
    return hasEntries ? 'blue' : 'default';
  }
  const normalized = normalizeStatusText(status);
  if (
    normalized.includes('error') ||
    normalized.includes('fail') ||
    normalized.includes('redo') ||
    normalized.includes('reject')
  ) {
    return 'red';
  }
  if (
    normalized.includes('pending') ||
    normalized.includes('wait') ||
    normalized.includes('running') ||
    normalized.includes('progress')
  ) {
    return 'gold';
  }
  if (
    normalized.includes('success') ||
    normalized.includes('ok') ||
    normalized.includes('pass') ||
    normalized.includes('complete') ||
    normalized.includes('finish')
  ) {
    return 'green';
  }
  if (normalized.includes('skip')) {
    return 'purple';
  }
  return 'geekblue';
};

const describeStepStatus = (step: ChapterPipelineStep) => {
  if (step.status) {
    return step.status;
  }
  if (step.entries.length) {
    return 'Completed';
  }
  return 'Not Started';
};

const buildChapterPipelineRuns = (
  entries: AdminLogEntry[],
  chapterNameMap: Record<string, string>,
  chapterWordCountMap: Record<string, number>
): ChapterPipelineRun[] => {
  if (!entries?.length) {
    return [];
  }

  type StepBuilder = {
    key: PipelineStepKey;
    label: string;
    entries: AdminLogEntry[];
    status?: string;
    startTimestamp?: string;
    endTimestamp?: string;
  };

  type RunBuilder = {
    key: string;
    runId?: string;
    chapterGuid?: string;
    targetLanguage?: string;
    sourceLanguage?: string;
    startTimestamp?: string;
    endTimestamp?: string;
    entries: AdminLogEntry[];
    stepMap: Map<PipelineStepKey, StepBuilder>;
    chunkIds: Set<number>;
  };

  const buildSubjectKey = (chapterGuid?: string, targetLanguage?: string) => {
    const chapterPart = (chapterGuid || 'chapter').trim() || 'chapter';
    const targetPart = (targetLanguage || 'target').trim() || 'target';
    return `${chapterPart}::${targetPart}`;
  };

  const sorted = [...entries].sort((a, b) => {
    const aTs = a.timestamp ? dayjs(a.timestamp).valueOf() : 0;
    const bTs = b.timestamp ? dayjs(b.timestamp).valueOf() : 0;
    return aTs - bTs;
  });

  const builders: RunBuilder[] = [];
  const builderByRunId = new Map<string, RunBuilder>();
  const activeRunBySubject = new Map<string, RunBuilder>();

  const isManifestRunId = (value?: string) => {
    if (!value) {
      return false;
    }
    const normalized = value.toLowerCase();
    return normalized.startsWith('translation_') || normalized.startsWith('transrun_');
  };

  const createBuilder = (
    entry: AdminLogEntry,
    builderKey: string
  ): RunBuilder => {
    const builder: RunBuilder = {
      key: builderKey,
      runId: entry.runId,
      chapterGuid: entry.chapterGuid || undefined,
      targetLanguage: entry.targetLanguage || undefined,
      sourceLanguage: entry.sourceLanguage || undefined,
      startTimestamp: entry.timestamp,
      endTimestamp: entry.timestamp,
      entries: [],
      stepMap: new Map(),
      chunkIds: new Set<number>(),
    };
    builders.push(builder);
    if (entry.runId) {
      builderByRunId.set(entry.runId, builder);
    }
    return builder;
  };

  const ensureBuilder = (entry: AdminLogEntry, index: number): RunBuilder => {
    const subjectKey = buildSubjectKey(entry.chapterGuid, entry.targetLanguage);
    if (entry.runId && isManifestRunId(entry.runId)) {
      const existing = builderByRunId.get(entry.runId);
      if (existing) {
        return existing;
      }
      return createBuilder(entry, `run-${entry.runId}`);
    }
    const activeSubjectRun = activeRunBySubject.get(subjectKey);
    if (activeSubjectRun) {
      return activeSubjectRun;
    }
    if (entry.runId) {
      return createBuilder(entry, `subject-${subjectKey}-${index}-${builders.length}`);
    }
    return createBuilder(entry, `chapter-${subjectKey}-${index}-${builders.length}`);
  };

  const updateStepBuilder = (step: StepBuilder, entry: AdminLogEntry) => {
    step.entries.push(entry);
    if (entry.timestamp) {
      if (!step.startTimestamp) {
        step.startTimestamp = entry.timestamp;
      }
      if (!step.endTimestamp || dayjs(entry.timestamp).isAfter(dayjs(step.endTimestamp))) {
        step.endTimestamp = entry.timestamp;
      }
    }
    if (entry.status) {
      step.status = entry.status;
    } else if (entry.statusSummary && !step.status) {
      step.status = entry.statusSummary;
    }
  };

  sorted.forEach((entry, index) => {
    const builder = ensureBuilder(entry, index);
    if (entry.runId && builder.runId !== entry.runId) {
      builder.runId = entry.runId;
      builderByRunId.set(entry.runId, builder);
    }
    builder.entries.push(entry);
    if (!builder.chapterGuid && entry.chapterGuid) {
      builder.chapterGuid = entry.chapterGuid;
    }
    if (!builder.targetLanguage && entry.targetLanguage) {
      builder.targetLanguage = entry.targetLanguage;
    }
    if (!builder.sourceLanguage && entry.sourceLanguage) {
      builder.sourceLanguage = entry.sourceLanguage;
    }
    const builderTimestamp = entry.timestamp ? dayjs(entry.timestamp) : null;
    if (builderTimestamp) {
      if (!builder.startTimestamp || builderTimestamp.isBefore(dayjs(builder.startTimestamp))) {
        builder.startTimestamp = entry.timestamp;
      }
      if (!builder.endTimestamp || builderTimestamp.isAfter(dayjs(builder.endTimestamp))) {
        builder.endTimestamp = entry.timestamp;
      }
    }
    if (typeof entry.chunkId === 'number') {
      builder.chunkIds.add(entry.chunkId);
    }
    if (builder.runId && isManifestRunId(builder.runId)) {
      const subjectKey = buildSubjectKey(builder.chapterGuid, builder.targetLanguage);
      activeRunBySubject.set(subjectKey, builder);
    }
    const stepKey = determinePipelineStepKey(entry);
    if (stepKey) {
      const existing = builder.stepMap.get(stepKey);
      const step =
        existing ||
        ({
          key: stepKey,
          label: PIPELINE_STEP_LABELS[stepKey],
          entries: [],
        } as StepBuilder);
      if (!existing) {
        builder.stepMap.set(stepKey, step);
      }
      updateStepBuilder(step, entry);
    }
  });

  return builders
    .map<ChapterPipelineRun | null>((builder) => {
      const pipelineSteps = PIPELINE_STEP_DEFINITIONS.map<ChapterPipelineStep>(
        ({ key, label }) => {
          const step = builder.stepMap.get(key);
          if (!step) {
            return { key, label, entries: [] };
          }
          let durationSeconds: number | undefined;
          if (step.startTimestamp && step.endTimestamp) {
            const start = dayjs(step.startTimestamp);
            const end = dayjs(step.endTimestamp);
            if (end.isAfter(start)) {
              durationSeconds = end.diff(start, 'second', true);
            }
          }
          return {
            key,
            label,
            entries: step.entries,
            status: step.status,
            startTimestamp: step.startTimestamp,
            endTimestamp: step.endTimestamp,
            durationSeconds,
          };
        }
      );
      const activeSteps = pipelineSteps.filter((step) => step.entries.length);
      const latestActiveStep = activeSteps[activeSteps.length - 1];
      const fallbackEntry = builder.entries[builder.entries.length - 1];
      const runStatus =
        latestActiveStep?.status ||
        latestActiveStep?.entries?.[latestActiveStep.entries.length - 1]?.status ||
        fallbackEntry?.status ||
        fallbackEntry?.statusSummary ||
        'unknown';
      const chapterGuid = builder.chapterGuid;
      const chapterName =
        (chapterGuid && chapterNameMap[chapterGuid]) || chapterGuid;
      const wordCount =
        chapterGuid != null ? chapterWordCountMap[chapterGuid] : undefined;
      return {
        id: builder.runId ? `run-${builder.runId}` : builder.key,
        runId: builder.runId,
        chapterGuid,
        chapterName,
        targetLanguage: builder.targetLanguage,
        sourceLanguage: builder.sourceLanguage,
        wordCount,
        startTimestamp: builder.startTimestamp,
        endTimestamp: builder.endTimestamp,
        status: runStatus || 'unknown',
        chunkCount: builder.chunkIds.size,
        totalEntries: builder.entries.length,
        pipelineSteps,
        entries: builder.entries,
      };
    })
    .filter((run): run is ChapterPipelineRun => !!run)
    .sort((a, b) => {
      const aTs = a.endTimestamp ? dayjs(a.endTimestamp).valueOf() : 0;
      const bTs = b.endTimestamp ? dayjs(b.endTimestamp).valueOf() : 0;
      return bTs - aTs;
    });
};

const describeLogStep = (entry: AdminLogEntry) => {
  const normalizedStage = (entry.stage || '').replace(/_/g, ' ');
  if (!entry.stage) {
    return { label: 'Log Entry', stage: normalizedStage, description: entry.status || '' };
  }

  if (normalizedStage.includes('alignment')) {
    if (entry.alignmentDetails?.reason) {
      return {
        label: 'Alignment Flag',
        stage: normalizedStage,
        description: entry.alignmentDetails.reason,
      };
    }
    return {
      label: 'Alignment Check',
      stage: normalizedStage,
      description: entry.status || '',
    };
  }

  if (normalizedStage.includes('validator_model_eval')) {
    return {
      label: 'Validator Model Evaluation',
      stage: normalizedStage,
      description: entry.status || '',
    };
  }

  if (normalizedStage.includes('validator')) {
    return {
      label: 'Validator Step',
      stage: normalizedStage,
      description: entry.status || '',
    };
  }

  return {
    label: normalizedStage || 'Log Entry',
    stage: normalizedStage,
    description: entry.status || '',
  };
};

const shouldHideTimelineEntry = (entry: AdminLogEntry) => {
  const normalizedStage = normalizeStageKey(entry.stage);
  const normalizedStatus = normalizeStatusText(entry.status);
  const hasDetail =
    (entry.replacements && entry.replacements.length > 0) ||
    !!entry.reply ||
    !!entry.snippet ||
    !!entry.fullText ||
    !!entry.statusSummary ||
    !!entry.alignmentDetails;
  if (!normalizedStage.includes('validator')) {
    return false;
  }
  if (hasDetail) {
    return false;
  }
  return normalizedStatus === '' || normalizedStatus === 'start' || normalizedStatus === 'unknown';
};

const AdminTranslationInsights: React.FC<AdminTranslationInsightsProps> = ({
  book,
  bookId,
  apiBase,
  auth,
  chapterWordCounts,
  isActive,
  isAdmin,
  onAskAI,
}) => {
  const [adminLogEntries, setAdminLogEntries] = useState<AdminLogEntry[]>([]);
  const [adminLogCount, setAdminLogCount] = useState(0);
  const [adminLogLoading, setAdminLogLoading] = useState(false);
  const [adminLogError, setAdminLogError] = useState<string | null>(null);
  const [adminLogUnavailable, setAdminLogUnavailable] = useState(false);
  const [adminLogChapterGuid, setAdminLogChapterGuid] = useState('');
  const [adminChapterInputValue, setAdminChapterInputValue] = useState('');
  const [adminLogModelId, setAdminLogModelId] = useState('');
  const [adminLogSince, setAdminLogSince] = useState<Dayjs | null>(null);
  const [adminLogLimit, setAdminLogLimit] = useState<number>(50);
  const [adminLogInitialized, setAdminLogInitialized] = useState(false);
  const [adminLogTargetLanguage, setAdminLogTargetLanguage] = useState('');
  const [logDetailEntry, setLogDetailEntry] = useState<AdminLogEntry | null>(null);
  const [translationCostSummary, setTranslationCostSummary] =
    useState<TranslationCostSummaryResponse | null>(null);
  const [translationCostLoading, setTranslationCostLoading] = useState(false);
  const [translationCostError, setTranslationCostError] = useState<string | null>(null);
  const [costDetailEntry, setCostDetailEntry] = useState<TranslationCostRunEntry | null>(null);
  const [selectedPipelineRun, setSelectedPipelineRun] = useState<ChapterPipelineRun | null>(null);
  const [integrityScanMode, setIntegrityScanMode] = useState<'quick' | 'full'>('quick');
  const [integrityScans, setIntegrityScans] = useState<TranslationIntegrityScanRecord[]>([]);
  const [integrityScanLoading, setIntegrityScanLoading] = useState(false);
  const [integrityScanSubmitting, setIntegrityScanSubmitting] = useState(false);
  const [integrityScanError, setIntegrityScanError] = useState<string | null>(null);
  const [integritySelectedScanId, setIntegritySelectedScanId] = useState('');
  const [integrityFindings, setIntegrityFindings] = useState<TranslationIntegrityFinding[]>([]);
  const [integrityFindingsLoading, setIntegrityFindingsLoading] = useState(false);
  const [integrityFindingsError, setIntegrityFindingsError] = useState<string | null>(null);
  const [integrityFindingsSeverity, setIntegrityFindingsSeverity] = useState<
    'high' | 'medium' | 'low' | 'clean' | ''
  >('');
  const [integrityDetailFinding, setIntegrityDetailFinding] =
    useState<TranslationIntegrityFinding | null>(null);

  const adminChapterOptions = useMemo<ChapterSuggestionOption[]>(() => {
    const chapters: Chapter[] = Array.isArray(book?.chapters) ? book.chapters : [];
    const seen = new Set<string>();
    return chapters.reduce<ChapterSuggestionOption[]>((acc, chapter) => {
      const guid = (chapter?.chapter_guid || '').trim();
      if (!guid || seen.has(guid)) {
        return acc;
      }
      seen.add(guid);
      let label = (chapter?.chapter_name || '').trim();
      if (!label) {
        label = chapter?.order != null ? `Chapter ${chapter.order}` : guid;
      }
      acc.push({
        value: label,
        label,
        guid,
      });
      return acc;
    }, []);
  }, [book?.chapters]);

  const chapterNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(book?.chapters)) {
      book.chapters.forEach((chapter: Chapter) => {
        if (chapter?.chapter_guid) {
          const label = (chapter.chapter_name || '').trim();
          map[chapter.chapter_guid] = label || chapter.chapter_guid;
        }
      });
    }
    return map;
  }, [book?.chapters]);

  const chapterWordCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (chapterWordCounts) {
      Object.entries(chapterWordCounts).forEach(([guid, counts]) => {
        if (guid && counts && typeof counts.original === 'number') {
          map[guid] = counts.original;
        }
      });
    }
    if (Array.isArray(book?.chapters)) {
      book.chapters.forEach((chapter: Chapter) => {
        if (chapter?.chapter_guid && typeof chapter.word_count === 'number') {
          if (map[chapter.chapter_guid] == null) {
            map[chapter.chapter_guid] = chapter.word_count;
          }
        }
      });
    }
    return map;
  }, [book?.chapters, chapterWordCounts]);

  const chapterPipelineRuns = useMemo(
    () => buildChapterPipelineRuns(adminLogEntries, chapterNameMap, chapterWordCountMap),
    [adminLogEntries, chapterNameMap, chapterWordCountMap]
  );

  const adminChapterNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    adminChapterOptions.forEach((option) => {
      lookup[option.label.trim().toLowerCase()] = option.guid;
    });
    return lookup;
  }, [adminChapterOptions]);

  const adminChapterGuidLabelLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    adminChapterOptions.forEach((option) => {
      lookup[option.guid] = option.label;
    });
    return lookup;
  }, [adminChapterOptions]);

  const adminTargetLanguageOptions = useMemo(() => {
    const targets = new Set<string>();
    const pairs = Array.isArray(book?.translation_pairs) ? book.translation_pairs : [];
    pairs.forEach((pair: any) => {
      if (typeof pair !== 'string') {
        return;
      }
      const target = getTargetLanguageFromPair(pair);
      if (target) {
        targets.add(target);
      }
    });
    return Array.from(targets).sort((a, b) => a.localeCompare(b));
  }, [book?.translation_pairs]);

  const sourceLanguage = useMemo(() => {
    const value = typeof book?.language === 'string' ? book.language.trim() : '';
    return value || 'English';
  }, [book?.language]);

  const activeTargetLanguage = useMemo(() => {
    const explicit = adminLogTargetLanguage.trim();
    if (explicit) {
      return explicit;
    }
    return adminTargetLanguageOptions[0] || '';
  }, [adminLogTargetLanguage, adminTargetLanguageOptions]);

  useEffect(() => {
    if (!adminLogChapterGuid) {
      if (adminChapterInputValue) {
        setAdminChapterInputValue('');
      }
      return;
    }
    const label = adminChapterGuidLabelLookup[adminLogChapterGuid];
    const desiredValue = label || adminLogChapterGuid;
    if (desiredValue !== adminChapterInputValue) {
      setAdminChapterInputValue(desiredValue);
    }
  }, [adminLogChapterGuid, adminChapterGuidLabelLookup, adminChapterInputValue]);

  const fetchAdminLogs = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setAdminLogLoading(true);
    setAdminLogError(null);

    const performFetch = async (stage: 'pipeline' | 'all') => {
      const params: Record<string, any> = {
        bookId,
        stage,
        limit: adminLogLimit,
      };
      if (adminLogChapterGuid.trim()) {
        params.chapterGuid = adminLogChapterGuid.trim();
      }
      if (adminLogModelId.trim()) {
        params.modelId = adminLogModelId.trim();
      }
      if (adminLogSince) {
        params.min_timestamp = adminLogSince.toISOString();
      }
      if (adminLogTargetLanguage.trim()) {
        params.targetLang = adminLogTargetLanguage.trim();
      }
      const headers = auth?.currentUser
        ? { Authorization: `Bearer ${await auth.currentUser.getIdToken()}` }
        : undefined;
      return axios.get<{ entries: AdminLogEntry[]; count: number }>(
        `${apiBase}/api/admin/ai-logs`,
        { params, headers }
      );
    };

    const normalizePayload = (entries?: AdminLogEntry[]) =>
      (entries || []).map((entry) => ({
        ...entry,
        replacements: entry.replacements || [],
      }));

    const applyResult = (data: { entries: AdminLogEntry[]; count: number }) => {
      const normalized = normalizePayload(data.entries);
      setAdminLogEntries(normalized);
      setAdminLogCount(data.count ?? normalized.length);
      if (!normalized.length) {
        message.info('No AI logs found for the current filters.');
      }
    };

    const shouldFallbackToAll = (error: any) => {
      if (error?.response?.status !== 400) {
        return false;
      }
      const detail = error?.response?.data?.detail;
      return typeof detail === 'string' && detail.toLowerCase().includes('stage');
    };

    try {
      const { data } = await performFetch('pipeline');
      applyResult(data);
    } catch (error: any) {
      if (shouldFallbackToAll(error)) {
        console.warn('Pipeline stage mode unsupported, falling back to legacy view.');
        try {
          const { data } = await performFetch('all');
          applyResult(data);
          message.info(
            'Pipeline log grouping is unavailable on this environment. Showing validator/proofreader logs only.'
          );
        } catch (fallbackError: any) {
          console.error('Failed to load AI logs (fallback)', fallbackError);
          const detail =
            fallbackError?.response?.data?.detail ||
            fallbackError?.message ||
            'Failed to load AI logs';
          setAdminLogError(
            typeof detail === 'string' ? detail : 'Failed to load AI logs'
          );
          if (fallbackError?.response?.status === 503) {
            setAdminLogUnavailable(true);
          }
        }
      } else {
        console.error('Failed to load AI logs', error);
        const detail =
          error?.response?.data?.detail || error?.message || 'Failed to load AI logs';
        setAdminLogError(typeof detail === 'string' ? detail : 'Failed to load AI logs');
        if (error?.response?.status === 503) {
          setAdminLogUnavailable(true);
        }
      }
    } finally {
      setAdminLogLoading(false);
    }
  }, [
    apiBase,
    adminLogChapterGuid,
    adminLogLimit,
    adminLogModelId,
    adminLogSince,
    adminLogTargetLanguage,
    auth,
    bookId,
    isAdmin,
  ]);

  const fetchTranslationCosts = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setTranslationCostLoading(true);
    setTranslationCostError(null);
    try {
      const headers = auth?.currentUser
        ? { Authorization: `Bearer ${await auth.currentUser.getIdToken()}` }
        : undefined;
      const targetLangParam = adminLogTargetLanguage.trim() || undefined;
      const { data } = await axios.get<TranslationCostSummaryResponse>(
        `${apiBase}/api/admin/translation-costs`,
        {
          params: { bookId, targetLang: targetLangParam },
          headers,
        }
      );
      setTranslationCostSummary(data);
    } catch (error: any) {
      console.error('Failed to load translation costs', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to load translation costs';
      setTranslationCostError(
        typeof detail === 'string' ? detail : 'Failed to load translation costs'
      );
    } finally {
      setTranslationCostLoading(false);
    }
  }, [apiBase, auth, bookId, isAdmin, adminLogTargetLanguage]);

  const fetchIntegrityScans = useCallback(async () => {
    if (!isAdmin || !activeTargetLanguage) {
      setIntegrityScans([]);
      return;
    }
    setIntegrityScanLoading(true);
    setIntegrityScanError(null);
    try {
      const headers = auth?.currentUser
        ? { Authorization: `Bearer ${await auth.currentUser.getIdToken()}` }
        : undefined;
      const { data } = await axios.get<TranslationIntegrityScanListResponse>(
        `${apiBase}/admin/translation-integrity-scans`,
        {
          params: {
            book_id: bookId,
            target_lang: activeTargetLanguage,
            limit: 20,
          },
          headers,
        }
      );
      const scans = data.items || [];
      setIntegrityScans(scans);
      if (!scans.length) {
        setIntegritySelectedScanId('');
        setIntegrityFindings([]);
        return;
      }
      setIntegritySelectedScanId((current) => {
        if (current && scans.some((scan) => scan.scanId === current)) {
          return current;
        }
        return scans[0].scanId;
      });
    } catch (error: any) {
      console.error('Failed to load translation integrity scans', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to load translation integrity scans';
      setIntegrityScanError(
        typeof detail === 'string' ? detail : 'Failed to load translation integrity scans'
      );
    } finally {
      setIntegrityScanLoading(false);
    }
  }, [activeTargetLanguage, apiBase, auth, bookId, isAdmin]);

  const fetchIntegrityFindings = useCallback(
    async (scanId: string) => {
      if (!isAdmin || !scanId) {
        setIntegrityFindings([]);
        return;
      }
      setIntegrityFindingsLoading(true);
      setIntegrityFindingsError(null);
      try {
        const headers = auth?.currentUser
          ? { Authorization: `Bearer ${await auth.currentUser.getIdToken()}` }
          : undefined;
        const params: Record<string, any> = { limit: 200, offset: 0 };
        if (integrityFindingsSeverity) {
          params.severity = integrityFindingsSeverity;
        }
        const { data } = await axios.get<TranslationIntegrityScanFindingsResponse>(
          `${apiBase}/admin/translation-integrity-scans/${scanId}/findings`,
          { params, headers }
        );
        setIntegrityFindings(data.items || []);
      } catch (error: any) {
        console.error('Failed to load translation integrity findings', error);
        const detail =
          error?.response?.data?.detail ||
          error?.message ||
          'Failed to load translation integrity findings';
        setIntegrityFindingsError(
          typeof detail === 'string' ? detail : 'Failed to load translation integrity findings'
        );
      } finally {
        setIntegrityFindingsLoading(false);
      }
    },
    [apiBase, auth, integrityFindingsSeverity, isAdmin]
  );

  const startIntegrityScan = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    if (!activeTargetLanguage) {
      message.error('Select a target language first.');
      return;
    }
    setIntegrityScanSubmitting(true);
    setIntegrityScanError(null);
    try {
      const headers = auth?.currentUser
        ? { Authorization: `Bearer ${await auth.currentUser.getIdToken()}` }
        : undefined;
      const chapterGuids = adminLogChapterGuid.trim() ? [adminLogChapterGuid.trim()] : undefined;
      const { data } = await axios.post<{ scan_id: string }>(
        `${apiBase}/admin/translation-integrity-scans`,
        {
          book_id: bookId,
          source_lang: sourceLanguage,
          target_lang: activeTargetLanguage,
          mode: integrityScanMode,
          chapter_guids: chapterGuids,
          force: false,
        },
        { headers }
      );
      const newScanId = data?.scan_id || '';
      if (newScanId) {
        setIntegritySelectedScanId(newScanId);
      }
      message.success('Translation integrity scan started.');
      await fetchIntegrityScans();
    } catch (error: any) {
      console.error('Failed to start translation integrity scan', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to start translation integrity scan';
      const text =
        typeof detail === 'string' ? detail : 'Failed to start translation integrity scan';
      setIntegrityScanError(text);
      message.error(text);
    } finally {
      setIntegrityScanSubmitting(false);
    }
  }, [
    activeTargetLanguage,
    adminLogChapterGuid,
    apiBase,
    auth,
    bookId,
    fetchIntegrityScans,
    integrityScanMode,
    isAdmin,
    sourceLanguage,
  ]);

  const resetAdminLogs = () => {
    setAdminLogChapterGuid('');
    setAdminChapterInputValue('');
    setAdminLogModelId('');
    setAdminLogSince(null);
    setAdminLogLimit(50);
    setAdminLogEntries([]);
    setAdminLogError(null);
    setAdminLogCount(0);
    setAdminLogUnavailable(false);
    setAdminLogInitialized(false);
    setAdminLogTargetLanguage('');
  };

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    if (!isActive) {
      setAdminLogInitialized(false);
      return;
    }
    if (!adminLogInitialized && !adminLogUnavailable) {
      fetchAdminLogs().finally(() => setAdminLogInitialized(true));
    }
  }, [isAdmin, isActive, adminLogInitialized, adminLogUnavailable, fetchAdminLogs]);

  useEffect(() => {
    if (!isAdmin || !isActive) {
      return;
    }
    fetchTranslationCosts();
  }, [isAdmin, isActive, adminLogTargetLanguage, fetchTranslationCosts]);

  useEffect(() => {
    if (!isAdmin || !isActive || !activeTargetLanguage) {
      return;
    }
    fetchIntegrityScans();
  }, [activeTargetLanguage, fetchIntegrityScans, isActive, isAdmin]);

  useEffect(() => {
    if (!isAdmin || !isActive || !integritySelectedScanId) {
      return;
    }
    fetchIntegrityFindings(integritySelectedScanId);
  }, [
    fetchIntegrityFindings,
    integrityFindingsSeverity,
    integritySelectedScanId,
    isActive,
    isAdmin,
  ]);

  useEffect(() => {
    setSelectedPipelineRun(null);
  }, [adminLogEntries]);

  const handleAskAIFromLog = useCallback(
    (entry: AdminLogEntry) => {
      if (!onAskAI || !entry.fullText) {
        return;
      }
      const stageLabel = (entry.stage || 'AI Log').replace(/_/g, ' ');
      const timestampLabel = entry.timestamp
        ? dayjs(entry.timestamp).format('MMM D, YYYY h:mm:ss A')
        : '';
      const variantLabel = timestampLabel ? `${stageLabel} – ${timestampLabel}` : stageLabel;
      const reviewContext =
        entry.replacements && entry.replacements.length
          ? {
              label: `${stageLabel} Replacements`,
              replacements: entry.replacements,
            }
          : null;
      onAskAI({
        chapterGuid: entry.chapterGuid || adminLogChapterGuid || '',
        variant: entry.stage ? `log_${entry.stage}` : 'ai_log',
        variantLabel,
        chapterHtml: entry.fullText,
        autoFetchReviewContext: false,
        initialReviewContext: reviewContext,
      });
    },
    [onAskAI, adminLogChapterGuid]
  );

  interface AggregatedReplacement {
    id: string;
    stageLabel: string;
    stageKey: string;
    timestamp?: string;
    from?: string;
    to?: string;
    method?: string;
    status?: string;
    proofreaderTask?: 'formality' | 'general';
  }

  const aggregatedReplacements = useMemo<AggregatedReplacement[]>(() => {
    if (!selectedPipelineRun) {
      return [];
    }
    const fallbackStageLabel =
      selectedPipelineRun.chapterName ||
      selectedPipelineRun.chapterGuid ||
      'Chapter Run';
    return selectedPipelineRun.entries.flatMap((entry, entryIndex) => {
      if (!entry.replacements || !entry.replacements.length) {
        return [];
      }
      const descriptor = describeLogStep(entry);
      const stageLabel =
        descriptor.label ||
        entry.stageLabel ||
        (entry.stage || fallbackStageLabel || 'Log Entry');
      const stageKey = entry.stage || descriptor.stage || stageLabel;
      const category = categorizeLogEntry(entry);
      const proofreaderTask =
        category === 'proofreader_general'
          ? 'general'
          : category === 'proofreader_formality'
          ? 'formality'
          : undefined;
      return entry.replacements!.map((rep, repIndex) => ({
        id: `${stageKey}-${entryIndex}-${repIndex}`,
        stageLabel,
        stageKey,
        timestamp: entry.timestamp,
        from: rep.from,
        to: rep.to,
        method: rep.method,
        status: rep.status || rep.reason,
        proofreaderTask,
      }));
    });
  }, [selectedPipelineRun]);

  const aggregatedReplacementSummaryText = useMemo(() => {
    if (!aggregatedReplacements.length) {
      return '';
    }
    return aggregatedReplacements
      .map((rep, idx) => {
        const timestampLabel = rep.timestamp ? formatAdminLogTimestamp(rep.timestamp) : '';
        const method = rep.method ? `Method: ${rep.method}` : '';
        const status = rep.status ? `Status: ${rep.status}` : '';
        const proofreaderLabel =
          rep.proofreaderTask === 'general'
            ? 'Proofreader: General'
            : rep.proofreaderTask === 'formality'
            ? 'Proofreader: Formality'
            : null;
        return [
          `${idx + 1}. Stage: ${rep.stageLabel}${timestampLabel ? ` (${timestampLabel})` : ''}`,
          `Source: ${rep.from || '(empty)'}`,
          `Replacement: ${rep.to || '(empty)'}`,
          proofreaderLabel,
          method || null,
          status || null,
        ]
          .filter(Boolean)
          .join('\n');
      })
      .join('\n\n');
  }, [aggregatedReplacements]);

  const baselineChapterHtml = useMemo(() => {
    if (!selectedPipelineRun) {
      return '';
    }
    const sortedEntries = selectedPipelineRun.entries
      .slice()
      .sort((a, b) => {
        const aTs = a.timestamp ? dayjs(a.timestamp).valueOf() : Number.MIN_SAFE_INTEGER;
        const bTs = b.timestamp ? dayjs(b.timestamp).valueOf() : Number.MIN_SAFE_INTEGER;
        return aTs - bTs;
      });
    const preferredEntry =
      sortedEntries.find(
        (entry) =>
          entry.fullText &&
          (entry.stage?.includes('proofreader.sample') ||
            entry.stage?.includes('proofread_before') ||
            entry.stage?.includes('validator.review_start'))
      ) || sortedEntries.find((entry) => entry.fullText);
    return preferredEntry?.fullText || '';
  }, [selectedPipelineRun]);

  const handleAskAIAboutReplacements = useCallback(() => {
    if (!onAskAI || !selectedPipelineRun) {
      return;
    }
    if (!aggregatedReplacements.length) {
      message.error('No replacements recorded for this run.');
      return;
    }
    if (!baselineChapterHtml.trim()) {
      message.error('No baseline translation text is available for this run.');
      return;
    }
    const pipelineLabel =
      selectedPipelineRun.chapterName ||
      selectedPipelineRun.chapterGuid ||
      'Chapter Run';
    const variantLabel = `${pipelineLabel} – Replacement Review`;
    const reviewContextSections: string[] = [];
    if (baselineChapterHtml.trim()) {
      reviewContextSections.push(
        `Base Translation (pre-validation):\n${baselineChapterHtml.trim()}`
      );
    }
    if (aggregatedReplacementSummaryText.trim()) {
      reviewContextSections.push(
        `Replacement Summary:\n${aggregatedReplacementSummaryText.trim()}`
      );
    }
    const reviewContextFullText =
      reviewContextSections.join('\n\n') ||
      aggregatedReplacementSummaryText ||
      'No replacement context recorded.';

    onAskAI({
      chapterGuid: selectedPipelineRun.chapterGuid || adminLogChapterGuid || '',
      variant: `ai_replacements_pipeline`,
      variantLabel,
      chapterHtml: baselineChapterHtml,
      initialReviewContext: {
        label: 'Validator & Proofreader Replacements',
        fullText: reviewContextFullText,
        replacements: aggregatedReplacements.map((rep) => ({
          from: `[${rep.stageLabel}] ${rep.from || ''}`,
          to: rep.to || '',
          method: rep.method,
        })),
      },
      autoFetchReviewContext: false,
    });
  }, [
    aggregatedReplacements,
    aggregatedReplacementSummaryText,
    adminLogChapterGuid,
    baselineChapterHtml,
    onAskAI,
    selectedPipelineRun,
  ]);

  const chapterPipelineColumns = useMemo<ColumnsType<ChapterPipelineRun>>(
    () => [
      {
        title: 'Chapter',
        key: 'chapter',
        width: 260,
        render: (_, run) => (
          <Space direction="vertical" size={0}>
            <Text strong>{run.chapterName || run.chapterGuid || '—'}</Text>
            <Text type="secondary">
              {(run.sourceLanguage || '—') + ' → ' + (run.targetLanguage || '—')}
            </Text>
            {typeof run.wordCount === 'number' ? (
              <Text type="secondary">{run.wordCount.toLocaleString()} words</Text>
            ) : null}
            {run.chunkCount ? (
              <Text type="secondary">
                {run.chunkCount} chunk{run.chunkCount === 1 ? '' : 's'}
              </Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: 'Pipeline Steps',
        key: 'pipeline',
        render: (_, run) => (
          <Space wrap size="small">
            {run.pipelineSteps.map((step) => (
              <Tag
                key={step.key}
                color={getStatusTagColor(step.status, !!step.entries.length)}
                style={{ marginBottom: 4 }}
              >
                {step.label}: {describeStepStatus(step)}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: 'Run Window',
        key: 'time',
        width: 220,
        render: (_, run) => (
          <Space direction="vertical" size={0}>
            <Text>{formatAdminLogTimestamp(run.startTimestamp)}</Text>
            {run.endTimestamp && run.endTimestamp !== run.startTimestamp ? (
              <Text type="secondary">{formatAdminLogTimestamp(run.endTimestamp)}</Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 200,
        render: (_, run) => (
          <Space direction="vertical" size={0}>
            <Tag color={getStatusTagColor(run.status, !!run.entries.length)}>
              {run.status || 'unknown'}
            </Tag>
            <Text type="secondary">
              {run.totalEntries} log step{run.totalEntries === 1 ? '' : 's'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, run) => (
          <Button size="small" onClick={() => setSelectedPipelineRun(run)}>
            View Steps
          </Button>
        ),
      },
    ],
    []
  );

  const translationCostColumns = useMemo<ColumnsType<TranslationCostRunEntry>>(
    () => [
      {
        title: 'Chapter',
        dataIndex: 'chapterGuid',
        key: 'chapterGuid',
        render: (_: string, record) =>
          chapterNameMap[record.chapterGuid || ''] || record.chapterGuid || '—',
      },
      {
        title: 'Words',
        dataIndex: 'chapterGuid',
        key: 'wordCount',
        render: (_: string, record) => {
          const count = chapterWordCountMap[record.chapterGuid || ''];
          return typeof count === 'number' ? count.toLocaleString() : '—';
        },
      },
      {
        title: 'Target',
        dataIndex: 'targetLang',
        key: 'targetLang',
      },
      {
        title: 'Total Cost',
        dataIndex: 'totalCostUsd',
        key: 'totalCostUsd',
        render: (value?: number) => formatUsd(value),
      },
      {
        title: 'Processing Time',
        dataIndex: 'processingSeconds',
        key: 'processingSeconds',
        render: (_: any, record) => formatDuration(record.processingSeconds),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: 'Details',
        key: 'actions',
        render: (_: any, record) => (
          <Button size="small" onClick={() => setCostDetailEntry(record)}>
            View Details
          </Button>
        ),
      },
    ],
    [chapterNameMap, chapterWordCountMap]
  );

  const integrityScanColumns = useMemo<ColumnsType<TranslationIntegrityScanRecord>>(
    () => [
      {
        title: 'Started',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        render: (value?: string) => formatAdminLogTimestamp(value),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (value: string) => (
          <Tag color={value === 'completed' ? 'green' : value === 'failed' ? 'red' : 'blue'}>
            {value || 'unknown'}
          </Tag>
        ),
      },
      {
        title: 'Progress',
        key: 'progress',
        render: (_: any, row) => {
          const total = row.progress?.totalChapters || 0;
          const done = row.progress?.completedChapters || 0;
          const failed = row.progress?.failedChapters || 0;
          return `${done}/${total}${failed ? ` (${failed} failed)` : ''}`;
        },
      },
      {
        title: 'Summary',
        key: 'summary',
        render: (_: any, row) => (
          <Space size={4} wrap>
            <Tag color="red">High {row.summary?.highRiskCount || 0}</Tag>
            <Tag color="orange">Med {row.summary?.mediumRiskCount || 0}</Tag>
            <Tag color="gold">Low {row.summary?.lowRiskCount || 0}</Tag>
            <Tag color="green">Clean {row.summary?.cleanCount || 0}</Tag>
          </Space>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        width: 120,
        render: (_: any, row) => (
          <Button size="small" onClick={() => setIntegritySelectedScanId(row.scanId)}>
            Findings
          </Button>
        ),
      },
    ],
    []
  );

  const integrityFindingColumns = useMemo<ColumnsType<TranslationIntegrityFinding>>(
    () => [
      {
        title: 'Chapter',
        dataIndex: 'chapterGuid',
        key: 'chapterGuid',
        width: 220,
        render: (_: string, row) =>
          row.chapterName || chapterNameMap[row.chapterGuid || ''] || row.chapterGuid || '—',
      },
      {
        title: 'Risk',
        dataIndex: 'riskLevel',
        key: 'riskLevel',
        width: 110,
        render: (value: string) => (
          <Tag color={getIntegrityRiskTagColor(value)}>{(value || 'clean').toUpperCase()}</Tag>
        ),
      },
      {
        title: 'Score',
        dataIndex: 'riskScore',
        key: 'riskScore',
        width: 90,
      },
      {
        title: 'Paragraphs',
        key: 'paragraphs',
        width: 130,
        render: (_: any, row) =>
          `${row.metrics?.sourceParagraphCount || 0} → ${row.metrics?.targetParagraphCount || 0}`,
      },
      {
        title: 'Sentence Coverage',
        key: 'coverage',
        width: 160,
        render: (_: any, row) => {
          const value = row.metrics?.sentenceCoverageEstimate;
          if (typeof value !== 'number') {
            return '—';
          }
          return `${(value * 100).toFixed(1)}%`;
        },
      },
      {
        title: 'Signals',
        key: 'signals',
        render: (_: any, row) => (
          <Space size={4} wrap>
            {(row.signals || []).slice(0, 4).map((signal) => (
              <Tag key={`${row.chapterGuid}-${signal}`}>{signal}</Tag>
            ))}
            {row.error ? <Tag color="red">error</Tag> : null}
          </Space>
        ),
      },
      {
        title: 'LLM Review',
        key: 'llmReview',
        width: 300,
        render: (_: any, row) => {
          const llm = row.evidence?.llmAdjudication;
          if (!llm) {
            return <Text type="secondary">—</Text>;
          }
          if (llm.status !== 'success') {
            return (
              <Space direction="vertical" size={2}>
                <Tag>LLM unavailable</Tag>
                {llm.error ? <Text type="secondary">{llm.error}</Text> : null}
              </Space>
            );
          }
          const confidence =
            typeof llm.confidence === 'number'
              ? `${(llm.confidence * 100).toFixed(1)}%`
              : '—';
          return (
            <Space direction="vertical" size={2}>
              <Space size={6} wrap>
                <Tag color={llm.missingContentLikely ? 'red' : 'green'}>
                  {llm.missingContentLikely ? 'Likely Missing' : 'No Missing Detected'}
                </Tag>
                <Tag>{confidence} confidence</Tag>
                {llm.modelId ? <Tag>{llm.modelId}</Tag> : null}
              </Space>
              {llm.reason ? (
                <Text type="secondary">{llm.reason.slice(0, 140)}</Text>
              ) : null}
              <Button size="small" type="link" onClick={() => setIntegrityDetailFinding(row)}>
                View
              </Button>
            </Space>
          );
        },
      },
    ],
    [chapterNameMap]
  );

  const filteredTranslationCostEntries = useMemo<TranslationCostRunEntry[]>(() => {
    if (!translationCostSummary?.entries) {
      return [];
    }
    const targetLangFilter = adminLogTargetLanguage.trim().toLowerCase();
    if (!targetLangFilter) {
      return translationCostSummary.entries;
    }
    return translationCostSummary.entries.filter(
      (entry) => (entry.targetLang || '').trim().toLowerCase() === targetLangFilter
    );
  }, [translationCostSummary, adminLogTargetLanguage]);

  const filteredTranslationCostTotal = useMemo<number>(() => {
    if (!filteredTranslationCostEntries.length) {
      return 0;
    }
    return filteredTranslationCostEntries.reduce(
      (sum, entry) => sum + (entry.totalCostUsd || 0),
      0
    );
  }, [filteredTranslationCostEntries]);

  const selectedIntegrityScan = useMemo(
    () =>
      integrityScans.find((scan) => scan.scanId === integritySelectedScanId) || null,
    [integrityScans, integritySelectedScanId]
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <Space className="admin-dashboard-surface" direction="vertical" size="middle" style={{ width: '100%', paddingTop: 12 }}>
      <Space wrap>
        <Select
          allowClear
          placeholder="Target language"
          value={adminLogTargetLanguage || undefined}
          onChange={(value) => setAdminLogTargetLanguage((value as string) || '')}
          style={{ minWidth: 180 }}
        >
          {adminTargetLanguageOptions.map((lang) => (
            <Option key={lang} value={lang}>
              {lang}
            </Option>
          ))}
        </Select>
        <AutoComplete
          placeholder="Chapter (optional)"
          value={adminChapterInputValue}
          allowClear
          options={adminChapterOptions}
          style={{ minWidth: 220 }}
          onSelect={(value, option) => {
            const selected = option as ChapterSuggestionOption;
            setAdminChapterInputValue(selected.label || (value as string));
            setAdminLogChapterGuid(selected.guid || (value as string));
          }}
          onChange={(value) => {
            const textValue = typeof value === 'string' ? value : '';
            setAdminChapterInputValue(textValue);
            const trimmed = textValue.trim();
            if (!trimmed) {
              setAdminLogChapterGuid('');
              return;
            }
            const lookup = adminChapterNameLookup[trimmed.toLowerCase()];
            setAdminLogChapterGuid(lookup || trimmed);
          }}
          filterOption={(inputValue, option) => {
            const label = ((option?.label as string) || '').toLowerCase();
            const guid = ((option as ChapterSuggestionOption | undefined)?.guid || '').toLowerCase();
            const needle = inputValue.trim().toLowerCase();
            return label.includes(needle) || guid.includes(needle);
          }}
        />
        <Input
          placeholder="Model ID (optional)"
          value={adminLogModelId}
          onChange={(e) => setAdminLogModelId(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <DatePicker
          showTime
          allowClear
          value={adminLogSince}
          onChange={(value) => setAdminLogSince(value)}
          placeholder="Since..."
        />
        <InputNumber
          min={1}
          value={adminLogLimit}
          onChange={(value) => setAdminLogLimit(typeof value === 'number' ? value : 50)}
          style={{ width: 120 }}
        />
        <Button
          type="primary"
          onClick={fetchAdminLogs}
          loading={adminLogLoading}
          disabled={adminLogUnavailable}
        >
          Fetch Logs
        </Button>
        <Button onClick={resetAdminLogs} disabled={adminLogUnavailable}>
          Reset
        </Button>
      </Space>
      {adminLogError ? <Alert type="error" showIcon message={adminLogError} /> : null}
      {adminLogUnavailable ? (
        <Alert
          type="info"
          showIcon
          message="AI logs are currently unavailable (Cloud Logging client not configured)."
        />
      ) : null}
      <Space
        direction="horizontal"
        style={{ width: '100%', justifyContent: 'space-between' }}
        size="small"
      >
        <Space direction="vertical" size={0}>
          <Text type="secondary">
            Showing {chapterPipelineRuns.length} run{chapterPipelineRuns.length === 1 ? '' : 's'} covering{' '}
            {adminLogEntries.length} log step{adminLogEntries.length === 1 ? '' : 's'} (of{' '}
            {adminLogCount} entries).
          </Text>
          <Text type="secondary">Target Language: {adminLogTargetLanguage || 'All'}</Text>
        </Space>
      </Space>
      <Card
        title="Translation Integrity Scan"
        extra={
          <Space>
            <Button onClick={fetchIntegrityScans} loading={integrityScanLoading}>
              Refresh
            </Button>
            <Button
              type="primary"
              onClick={startIntegrityScan}
              loading={integrityScanSubmitting}
              disabled={!activeTargetLanguage}
            >
              Start Scan
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Text type="secondary">Source: {sourceLanguage}</Text>
            <Text type="secondary">
              Target: {activeTargetLanguage || 'Select a target language'}
            </Text>
            <Select
              value={integrityScanMode}
              style={{ width: 140 }}
              onChange={(value) => setIntegrityScanMode((value as 'quick' | 'full') || 'quick')}
              options={[
                { label: 'Quick', value: 'quick' },
                { label: 'Full', value: 'full' },
              ]}
            />
            <Select
              value={integrityFindingsSeverity}
              allowClear
              placeholder="Findings severity"
              style={{ minWidth: 180 }}
              onChange={(value) =>
                setIntegrityFindingsSeverity((value as 'high' | 'medium' | 'low' | 'clean') || '')
              }
              options={[
                { label: 'High', value: 'high' },
                { label: 'Medium', value: 'medium' },
                { label: 'Low', value: 'low' },
                { label: 'Clean', value: 'clean' },
              ]}
            />
          </Space>
          {integrityScanError ? (
            <Alert type="error" showIcon message={integrityScanError} />
          ) : null}
          {integrityFindingsError ? (
            <Alert type="error" showIcon message={integrityFindingsError} />
          ) : null}
          <Table<TranslationIntegrityScanRecord>
            columns={integrityScanColumns}
            dataSource={integrityScans}
            rowKey={(scan) => scan.scanId}
            pagination={{ pageSize: 5 }}
            loading={integrityScanLoading}
            size="small"
          />
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>
              Findings for scan: {selectedIntegrityScan?.scanId || '—'}
            </Text>
            {selectedIntegrityScan ? (
              <Space size={6} wrap>
                <Tag color="red">High {selectedIntegrityScan.summary?.highRiskCount || 0}</Tag>
                <Tag color="orange">Medium {selectedIntegrityScan.summary?.mediumRiskCount || 0}</Tag>
                <Tag color="gold">Low {selectedIntegrityScan.summary?.lowRiskCount || 0}</Tag>
                <Tag color="green">Clean {selectedIntegrityScan.summary?.cleanCount || 0}</Tag>
              </Space>
            ) : null}
          </Space>
          <Table<TranslationIntegrityFinding>
            columns={integrityFindingColumns}
            dataSource={integrityFindings}
            rowKey={(finding) => finding.chapterGuid}
            pagination={{ pageSize: 8 }}
            loading={integrityFindingsLoading}
            size="small"
          />
        </Space>
      </Card>
      <Card
        title="Translation Costs"
        style={{ marginTop: 16 }}
        extra={
          <Button onClick={() => fetchTranslationCosts()} loading={translationCostLoading}>
            Refresh
          </Button>
        }
      >
        {translationCostError ? (
          <Alert
            type="error"
            showIcon
            message={translationCostError}
            style={{ marginBottom: 12 }}
          />
        ) : null}
        {translationCostSummary ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text strong>
              Book Total{adminLogTargetLanguage ? ' (filtered)' : ''}:{' '}
              {formatUsd(
                adminLogTargetLanguage
                  ? filteredTranslationCostTotal
                  : typeof translationCostSummary.totalCostUsd === 'number'
                  ? translationCostSummary.totalCostUsd
                  : undefined
              )}
            </Text>
            <Table<TranslationCostRunEntry>
              columns={translationCostColumns}
              dataSource={filteredTranslationCostEntries}
              rowKey={(run) =>
                `${run.chapterGuid}-${run.targetLang}-${run.runId || run.id || 'latest'}`
              }
              pagination={{ pageSize: 5 }}
              loading={translationCostLoading}
              size="small"
            />
          </Space>
        ) : translationCostLoading ? (
          <Spin />
        ) : (
          <Text type="secondary">No translation cost data recorded yet.</Text>
        )}
      </Card>
      <Table<ChapterPipelineRun>
        columns={chapterPipelineColumns}
        dataSource={chapterPipelineRuns}
        rowKey={(run) => run.id}
        pagination={{ pageSize: 10 }}
        loading={adminLogLoading}
        scroll={{ x: 1100 }}
        size="middle"
      />
      <Modal
        open={!!integrityDetailFinding}
        onCancel={() => setIntegrityDetailFinding(null)}
        footer={<Button onClick={() => setIntegrityDetailFinding(null)}>Close</Button>}
        width={780}
        title="Translation Integrity Finding Details"
      >
        {integrityDetailFinding ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Chapter">
                {integrityDetailFinding.chapterName ||
                  chapterNameMap[integrityDetailFinding.chapterGuid || ''] ||
                  integrityDetailFinding.chapterGuid ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Risk">
                <Tag color={getIntegrityRiskTagColor(integrityDetailFinding.riskLevel)}>
                  {(integrityDetailFinding.riskLevel || 'clean').toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Risk Score">
                {integrityDetailFinding.riskScore ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Signals">
                {(integrityDetailFinding.signals || []).join(', ') || '—'}
              </Descriptions.Item>
            </Descriptions>
            {integrityDetailFinding.evidence?.llmAdjudication ? (
              <Card size="small" title="LLM Adjudication">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space size={6} wrap>
                    <Tag>
                      Status:{' '}
                      {integrityDetailFinding.evidence.llmAdjudication.status || 'unknown'}
                    </Tag>
                    {integrityDetailFinding.evidence.llmAdjudication.modelId ? (
                      <Tag>{integrityDetailFinding.evidence.llmAdjudication.modelId}</Tag>
                    ) : null}
                    {typeof integrityDetailFinding.evidence.llmAdjudication.confidence ===
                    'number' ? (
                      <Tag>
                        Confidence:{' '}
                        {(
                          integrityDetailFinding.evidence.llmAdjudication.confidence * 100
                        ).toFixed(1)}
                        %
                      </Tag>
                    ) : null}
                    {typeof integrityDetailFinding.evidence.llmAdjudication
                      .missingContentLikely === 'boolean' ? (
                      <Tag
                        color={
                          integrityDetailFinding.evidence.llmAdjudication
                            .missingContentLikely
                            ? 'red'
                            : 'green'
                        }
                      >
                        {integrityDetailFinding.evidence.llmAdjudication
                          .missingContentLikely
                          ? 'Likely Missing Content'
                          : 'No Missing Content Detected'}
                      </Tag>
                    ) : null}
                  </Space>
                  {integrityDetailFinding.evidence.llmAdjudication.reason ? (
                    <div>
                      <Text strong>Reason:</Text>
                      <div>{integrityDetailFinding.evidence.llmAdjudication.reason}</div>
                    </div>
                  ) : null}
                  {integrityDetailFinding.evidence.llmAdjudication.coverageSummary ? (
                    <div>
                      <Text strong>Coverage Summary:</Text>
                      <div>{integrityDetailFinding.evidence.llmAdjudication.coverageSummary}</div>
                    </div>
                  ) : null}
                  {(integrityDetailFinding.evidence.llmAdjudication.missingEvidence || []).length ? (
                    <div>
                      <Text strong>Missing Evidence Samples:</Text>
                      <List
                        size="small"
                        bordered
                        dataSource={
                          integrityDetailFinding.evidence.llmAdjudication.missingEvidence || []
                        }
                        renderItem={(item) => <List.Item>{item}</List.Item>}
                      />
                    </div>
                  ) : null}
                  {integrityDetailFinding.evidence.llmAdjudication.error ? (
                    <Alert
                      type="warning"
                      showIcon
                      message={integrityDetailFinding.evidence.llmAdjudication.error}
                    />
                  ) : null}
                </Space>
              </Card>
            ) : null}
            {(integrityDetailFinding.evidence?.missingLikeSpans || []).length ? (
              <Card size="small" title="Heuristic Missing-Like Spans">
                <List
                  size="small"
                  bordered
                  dataSource={integrityDetailFinding.evidence?.missingLikeSpans || []}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </Card>
            ) : null}
          </Space>
        ) : null}
      </Modal>
      <Modal
        open={!!selectedPipelineRun}
        onCancel={() => setSelectedPipelineRun(null)}
        footer={<Button onClick={() => setSelectedPipelineRun(null)}>Close</Button>}
        width={900}
        title={
          selectedPipelineRun
            ? `Pipeline Run – ${
                chapterNameMap[selectedPipelineRun.chapterGuid || ''] ||
                selectedPipelineRun.chapterGuid ||
                '—'
              }`
            : 'Run Details'
        }
      >
        {selectedPipelineRun ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Chapter">
                {chapterNameMap[selectedPipelineRun.chapterGuid || ''] ||
                  selectedPipelineRun.chapterGuid ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Languages">
                {(selectedPipelineRun.sourceLanguage || '—') +
                  ' → ' +
                  (selectedPipelineRun.targetLanguage || '—')}
              </Descriptions.Item>
              <Descriptions.Item label="Run ID">
                {selectedPipelineRun.runId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Word Count">
                {typeof selectedPipelineRun.wordCount === 'number'
                  ? selectedPipelineRun.wordCount.toLocaleString()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Start">
                {formatAdminLogTimestamp(selectedPipelineRun.startTimestamp)}
              </Descriptions.Item>
              <Descriptions.Item label="End">
                {formatAdminLogTimestamp(selectedPipelineRun.endTimestamp)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {selectedPipelineRun.status || 'unknown'}
              </Descriptions.Item>
              <Descriptions.Item label="Chunks / Entries">
                {selectedPipelineRun.chunkCount} chunk{selectedPipelineRun.chunkCount === 1 ? '' : 's'} ·{' '}
                {selectedPipelineRun.totalEntries}{' '}
                {selectedPipelineRun.totalEntries === 1 ? 'entry' : 'entries'}
              </Descriptions.Item>
            </Descriptions>
            {aggregatedReplacements.length ? (
              <Card
                size="small"
                title={`AI-Suggested Replacements (${aggregatedReplacements.length})`}
                extra={
                  onAskAI ? (
                    <Tooltip
                      title={
                        baselineChapterHtml.trim()
                          ? undefined
                          : 'No baseline translation text was captured for this run.'
                      }
                    >
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleAskAIAboutReplacements}
                        disabled={!baselineChapterHtml.trim()}
                      >
                        Ask AI about replacements
                      </Button>
                    </Tooltip>
                  ) : null
                }
              >
                <List
                  size="small"
                  dataSource={aggregatedReplacements}
                  style={{ maxHeight: 320, overflowY: 'auto' }}
                  renderItem={(rep, idx) => (
                    <List.Item key={rep.id} style={{ alignItems: 'flex-start' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text strong>
                          {idx + 1}. {rep.stageLabel}
                        </Text>
                        {rep.timestamp ? (
                          <Text type="secondary">{formatAdminLogTimestamp(rep.timestamp)}</Text>
                        ) : null}
                        <div>
                          <Text strong>Source:</Text>{' '}
                          {(rep.from || '').slice(0, 400) || '(empty)'}
                        </div>
                        <div>
                          <Text strong>Replacement:</Text>{' '}
                          {(rep.to || '').slice(0, 400) || '(empty)'}
                        </div>
                        <Space size="small" wrap>
                          {rep.proofreaderTask === 'formality' ? (
                            <Tag color="purple">Formality Proofreader</Tag>
                          ) : null}
                          {rep.proofreaderTask === 'general' ? (
                            <Tag color="green">General Proofreader</Tag>
                          ) : null}
                          {rep.method ? <Tag>{rep.method}</Tag> : null}
                          {rep.status ? <Tag color="blue">{rep.status}</Tag> : null}
                        </Space>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            ) : null}
            <Timeline style={{ paddingLeft: 8 }}>
              {selectedPipelineRun.pipelineSteps.map((step) => (
                <Timeline.Item
                  key={step.key}
                  color={
                    step.entries.length
                      ? getStatusTagColor(step.status, !!step.entries.length)
                      : 'gray'
                  }
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Text strong>{step.label}</Text>
                      {step.startTimestamp ? (
                        <Text type="secondary">
                          {formatAdminLogTimestamp(step.startTimestamp)}
                        </Text>
                      ) : null}
                    </Space>
                    <Text type="secondary">Status: {describeStepStatus(step)}</Text>
                    {typeof step.durationSeconds === 'number' ? (
                      <Text type="secondary">
                        Duration: {formatDuration(step.durationSeconds)}
                      </Text>
                    ) : null}
                    {step.entries.length ? (
                      step.entries
                        .filter((entry) => !shouldHideTimelineEntry(entry))
                        .map((entry, index) => {
                          const descriptor = describeLogStep(entry);
                          return (
                            <Card
                              key={`${step.key}-${entry.timestamp || entry.stage || index}`}
                              size="small"
                            style={{ width: '100%' }}
                          >
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                <Text strong>{descriptor.label}</Text>
                                <Text type="secondary">
                                  {formatAdminLogTimestamp(entry.timestamp)}
                                </Text>
                              </Space>
                              {descriptor.description ? (
                                <Text type="secondary">{descriptor.description}</Text>
                              ) : null}
                              {descriptor.stage && descriptor.stage !== descriptor.label ? (
                                <Text type="secondary">Stage: {descriptor.stage}</Text>
                              ) : null}
                              <Text type="secondary">Status: {entry.status || 'unknown'}</Text>
                              {entry.alignmentDetails?.reason ? (
                                <Text type="secondary">
                                  Alignment reason: {entry.alignmentDetails.reason}
                                </Text>
                              ) : null}
                              {entry.alignmentCheckId ? (
                                <Text type="secondary">
                                  Check ID: {entry.alignmentCheckId}
                                </Text>
                              ) : null}
                              {entry.snippet ? (
                                <Text type="secondary">
                                  Snippet: {(entry.snippet || '').slice(0, 200)}
                                </Text>
                              ) : null}
                              <Space size="small" wrap>
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() => setLogDetailEntry(entry)}
                                >
                                  View Details
                                </Button>
                                {onAskAI && entry.fullText ? (
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={() => handleAskAIFromLog(entry)}
                                  >
                                    Ask AI
                                  </Button>
                                ) : null}
                                {entry.replacements && entry.replacements.length ? (
                                  <Tag color="blue">
                                    {entry.replacements.length} replacement
                                    {entry.replacements.length === 1 ? '' : 's'}
                                  </Tag>
                                ) : null}
                              </Space>
                            </Space>
                          </Card>
                        );
                      })
                    ) : (
                      <Text type="secondary">No log entries recorded for this step.</Text>
                    )}
                    {step.entries.length &&
                    !step.entries.some((entry) => !shouldHideTimelineEntry(entry)) ? (
                      <Text type="secondary">
                        No detailed log entries recorded for this step.
                      </Text>
                    ) : (
                      null
                    )}
                  </Space>
                </Timeline.Item>
              ))}
            </Timeline>
          </Space>
        ) : null}
      </Modal>
      <Modal
        open={!!logDetailEntry}
        onCancel={() => setLogDetailEntry(null)}
        footer={<Button onClick={() => setLogDetailEntry(null)}>Close</Button>}
        width={800}
        title={
          logDetailEntry
            ? `${(logDetailEntry.stage || 'Log').replace(/_/g, ' ')} Details`
            : 'Log Details'
        }
      >
        {logDetailEntry && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Timestamp:</Text>{' '}
              {logDetailEntry.timestamp
                ? dayjs(logDetailEntry.timestamp).format('MMM D, YYYY h:mm:ss A')
                : '—'}
            </div>
            <div>
              <Text strong>Chapter:</Text>{' '}
              {chapterNameMap[logDetailEntry.chapterGuid || ''] ||
                logDetailEntry.chapterGuid ||
                '—'}
            </div>
            <div>
              <Text strong>Full Text:</Text>
              <div
                style={{
                  maxHeight: 260,
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  padding: 8,
                  borderRadius: 4,
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {logDetailEntry.fullText ||
                    logDetailEntry.snippet ||
                    '(no text logged)'}
                </pre>
              </div>
            </div>
            <div>
              <Text strong>Reply:</Text>
              <div
                style={{
                  maxHeight: 260,
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  padding: 8,
                  borderRadius: 4,
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {logDetailEntry.reply || '(no reply logged)'}
                </pre>
              </div>
            </div>
            {logDetailEntry.system_prompt_preview ? (
              <div>
                <Text strong>System Prompt Preview:</Text>
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {logDetailEntry.system_prompt_preview}
                  </pre>
                </div>
              </div>
            ) : null}
            {logDetailEntry.user_prompt_preview ? (
              <div>
                <Text strong>User Prompt Preview:</Text>
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {logDetailEntry.user_prompt_preview}
                  </pre>
                </div>
              </div>
            ) : null}
            <div>
              <Text strong>Replacements:</Text>
              {logDetailEntry.replacements && logDetailEntry.replacements.length ? (
                <List
                  bordered
                  size="small"
                  dataSource={logDetailEntry.replacements}
                  renderItem={(rep, idx) => (
                    <List.Item key={`${idx}-${rep.from}-${rep.to}`}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <div>
                          <Text strong>{rep.from || '(source)'}</Text> →{' '}
                          <Text strong>{rep.to || '(target)'}</Text>
                        </div>
                        <Space size="small">
                          {rep.method ? (
                            <Text type="secondary">{rep.method}</Text>
                          ) : null}
                          {rep.reason ? (
                            <Text type="secondary">Reason: {rep.reason}</Text>
                          ) : null}
                          {rep.details ? (
                            <Text type="secondary">{rep.details}</Text>
                          ) : null}
                          {typeof rep.count === 'number' ? (
                            <Text type="secondary">Count: {rep.count}</Text>
                          ) : null}
                        </Space>
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <Text type="secondary">No replacements logged.</Text>
              )}
            </div>
          </Space>
        )}
      </Modal>
      <Modal
        open={!!costDetailEntry}
        onCancel={() => setCostDetailEntry(null)}
        footer={<Button onClick={() => setCostDetailEntry(null)}>Close</Button>}
        width={720}
        title="Translation Cost Details"
      >
        {costDetailEntry ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Chapter">
                {chapterNameMap[costDetailEntry.chapterGuid || ''] ||
                  costDetailEntry.chapterGuid ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Language">
                {costDetailEntry.targetLang || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {costDetailEntry.status || 'unknown'}
              </Descriptions.Item>
              <Descriptions.Item label="Total Cost">
                {formatUsd(costDetailEntry.totalCostUsd)}
              </Descriptions.Item>
              <Descriptions.Item label="Translation">
                {formatUsd(costDetailEntry.translationCostUsd)}
              </Descriptions.Item>
              <Descriptions.Item label="Validation">
                {formatUsd(costDetailEntry.validationCostUsd)}
              </Descriptions.Item>
              <Descriptions.Item label="Proofreader">
                {formatUsd(costDetailEntry.proofreaderCostUsd)}
              </Descriptions.Item>
              <Descriptions.Item label="Processing Time">
                {formatDuration(costDetailEntry.processingSeconds)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Run">
                {formatAdminLogTimestamp(costDetailEntry.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
            {costDetailEntry.stageBreakdown?.map((stage) => {
              const isAlignmentStage = normalizeStageKey(stage.stage) === 'alignment';
              return (
                <Card key={stage.stage} size="small">
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Text strong>{formatStageHeading(stage.stage)}</Text>
                      <Text>{formatUsd(stage.costUsd)}</Text>
                    </Space>
                    <Text type="secondary">
                      Tokens: {stage.promptTokens || 0} in / {stage.completionTokens || 0} out
                    </Text>
                    <Text type="secondary">
                      Duration: {formatDuration(stage.durationSeconds)}
                    </Text>
                    {stage.subStages && stage.subStages.length ? (
                      <div>
                        <Text strong>Sub-steps:</Text>
                        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                          {stage.subStages.map((sub) => (
                            <li key={`${stage.stage}-${sub.label}`}>
                              {formatSubStageLabel(stage.stage, sub.label)} — {formatUsd(sub.costUsd)} (
                              {sub.promptTokens || 0} / {sub.completionTokens || 0}) ·{' '}
                              {formatDuration(sub.durationSeconds)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {isAlignmentStage && costDetailEntry.alignmentFinalRan === false ? (
                      <Text type="secondary" style={{ fontStyle: 'italic' }}>
                        Final alignment (post-proofread) was not executed (no proofreader changes required).
                      </Text>
                    ) : null}
                  </Space>
                </Card>
              );
            })}
          </Space>
        ) : (
          <Text type="secondary">No stage data available.</Text>
        )}
      </Modal>
    </Space>
  );
};

export default AdminTranslationInsights;
